import React from "react";
import Reveal from "../Reveal";
import SpotlightCard from "../SpotlightCard";

const FACTS = [
  { icon: "fa-solid fa-location-dot", text: "San Francisco, CA" },
  { icon: "fa-solid fa-briefcase", text: "Currently leading engineering at Syntrillo" },
  { icon: "fa-solid fa-arrows-rotate", text: "Career switcher — finance to software" },
];

const TIMELINE = [
  {
    icon: "fa-solid fa-briefcase",
    role: "Software Engineer, Lead",
    org: "Syntrillo",
    date: "Dec 2024 — Present",
    points: [
      "Building HIPAA-compliant ETL/ELT pipelines that unify PHI across clinical data sources.",
      "Own the team's AWS infrastructure end-to-end — Lambda, RDS, CodePipeline/CodeBuild, CloudWatch.",
      "Led migration off a legacy monolith to microservices, cutting infrastructure cost and technical debt.",
    ],
  },
  {
    icon: "fa-solid fa-briefcase",
    role: "Software Developer",
    org: "Food Access LA",
    date: "Apr 2024 — Mar 2025",
    points: [
      "Built 90% of the React frontend and owned the AWS Elastic Beanstalk backend infrastructure.",
      "Cut deploy time 70% with a Git-to-CodePipeline CI/CD setup and improved load times 25% with a Zustand rewrite.",
    ],
  },
  {
    icon: "fa-solid fa-graduation-cap",
    role: "Full Stack Web Development",
    org: "App Academy",
    date: "2023",
    points: [],
  },
  {
    icon: "fa-solid fa-briefcase",
    role: "Investment Analyst",
    org: "Canterbury Consulting",
    date: "Mar 2021 — Feb 2023",
    points: [
      "Supported $1.2B in AUM across portfolio analysis, rebalancing, and reporting for institutional clients.",
    ],
  },
  {
    icon: "fa-solid fa-graduation-cap",
    role: "B.S. Finance",
    org: "California State University, Long Beach",
    date: "2021",
    points: [],
  },
];

export default function About() {
  return (
    <section id="about" className="relative py-28 px-6">
      <div className="max-w-4xl mx-auto">
        <Reveal>
          <span className="text-accent-light text-sm font-semibold tracking-widest uppercase">
            About
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-10">
            A bit about me
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-[1.6fr_1fr] gap-10">
          <Reveal delay={100} className="flex flex-col gap-5 text-zinc-400 text-base md:text-lg leading-relaxed">
            <p>
              I&apos;m a full-stack engineer with a background in finance. Studying markets
              and investments taught me to think in systems and tradeoffs — a habit that
              carried straight over into how I approach building software.
            </p>
            <p>
              Today, I lead engineering at a healthtech startup, building the data
              infrastructure and clinical tools physicians rely on for real patient care
              decisions. I like owning problems end-to-end — architecture, infrastructure,
              and the last 10% of polish that makes a feature actually good.
            </p>
            <p className="text-zinc-500">
              Arsenal, 49ers, and Lakers fan. <span className="text-zinc-400">#COYG</span>
            </p>
          </Reveal>

          <Reveal delay={200} className="flex flex-col gap-4">
            {FACTS.map((fact) => (
              <SpotlightCard
                key={fact.text}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 text-zinc-300 transition-colors"
              >
                <span className="w-8 h-8 rounded-lg bg-accent/15 text-accent-light flex items-center justify-center shrink-0">
                  <i className={fact.icon}></i>
                </span>
                <span className="text-sm md:text-base pt-1">{fact.text}</span>
              </SpotlightCard>
            ))}
          </Reveal>
        </div>

        <div id="experience" className="mt-20 pt-16 border-t border-white/5 scroll-mt-16">
          <Reveal>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-10">
              Where I&apos;ve been
            </h3>
          </Reveal>

          <div className="relative border-l border-white/10 pl-7 sm:pl-9 flex flex-col gap-6">
            {TIMELINE.map((item, i) => (
              <Reveal key={`${item.role}-${item.org}`} delay={i * 80} className="relative">
                <span className="absolute -left-[38px] sm:-left-[46px] top-3.5 w-7 h-7 rounded-full bg-zinc-900 border border-white/10 text-accent-light flex items-center justify-center text-xs">
                  <i className={item.icon}></i>
                </span>
                <div className="rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 p-4 sm:p-5 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-2">
                    <h4 className="text-white font-semibold">
                      {item.role} <span className="text-zinc-500 font-normal">· {item.org}</span>
                    </h4>
                    <span className="text-zinc-500 text-sm shrink-0">{item.date}</span>
                  </div>
                  {item.points.length > 0 && (
                    <ul className="flex flex-col gap-1.5">
                      {item.points.map((point) => (
                        <li
                          key={point}
                          className="text-zinc-400 text-sm md:text-base leading-relaxed flex gap-2"
                        >
                          <span className="text-accent-light">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
